import axios from 'axios';

import { isDeepEqual } from '@/helpers/comparison';
import { formatAndThrowApiError } from '@/helpers/api';

export const mutateListOnPost = (currentObjs, newObj) => ({
  content: Object.fromEntries([...Object.entries(currentObjs.content), [newObj.uid, newObj]]),
  query: [...new Set([...currentObjs.query, newObj.uid])]
});

export const bulkMutateListOnPost = (currentObjs, newObjs) => ({
  content: Object.fromEntries([...Object.entries(currentObjs.content), ...newObjs.map((obj) => [obj.uid, obj])]),
  query: [...new Set([...currentObjs.query, ...newObjs.map((obj) => obj.uid)])]
});

export const mutateListOnPut = (currentObjs, updatedObj, keyFn = (obj) => obj.uid) => ({
  content: Object.fromEntries(Object.entries(currentObjs.content).map(([key, value]) => (key === keyFn(updatedObj) ? [key, updatedObj] : [key, value]))),
  query: currentObjs.query
});

export const bulkMutateListOnPut = (currentObjs, updatedObjs, keyFn = (obj) => obj.uid) => {
  const updatesByKey = new Map(updatedObjs.map((obj) => [keyFn(obj), obj]));
  return {
    content: Object.fromEntries(Object.entries(currentObjs.content).map(([key, value]) => (updatesByKey.has(key) ? [key, updatesByKey.get(key)] : [key, value]))),
    query: currentObjs.query
  };
};

// A reorder rewrites `query` and nothing else — `mutateListOnPut` deliberately
// leaves `query` alone, so none of the other helpers cover it. Reordering a list
// nested inside one record is not this: that replaces a single `content` entry,
// which is already `mutateListOnPut`.
export const mutateListOnReorder = (currentObjs, newQuery) => ({
  content: currentObjs.content,
  query: newQuery
});

export const mutateListOnDelete = (currentObjs, deletedUid) => ({
  content: Object.fromEntries(Object.entries(currentObjs.content).filter(([key]) => key !== deletedUid)),
  query: currentObjs.query.filter((uid) => uid !== deletedUid)
});

export const localSwrConfig = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  revalidateOnMount: false,
  revalidateOnWindowFocus: false,
  revalidateIfStale: false,
  revalidateOnRevalidate: false,
  revalidateFirstPage: false,
  dedupingInterval: Infinity
};

// The `compare` for a `use<Entity>Object(uid)` read. SWR's default deep-equals the whole list once per
// subscriber on every change to it, which on a large Detailed Budget is hundreds of whole-list walks per
// edit. The `mutateListOn*` helpers keep every entry they do not touch by reference, so a reader whose
// entry is still the same object is answered in O(1), and only the edited entity's readers go on to the
// deep compare.
//
// Anything else falls back to that deep compare, which is SWR's default answer. That matters because SWR
// also asks this `compare`, when the reader is the one that ran a revalidation, whether to keep the cached
// list over the fetched one: a fetched list never shares an entry with the cache, so the answer there is
// exactly the default's, and a refetch of unchanged data keeps the cache and re-renders nobody.
export const isSameListEntry = (a, b, uid) => a === b || (a?.content[uid] !== undefined && a.content[uid] === b?.content[uid]) || isDeepEqual(a, b);

// The fetcher for every list read in `src/data/`. SWR passes the key as the fetcher's argument, and every
// key there is the request URL, so a list hook and the `*Object` hooks that read it share this.
//
// A failed request the API answered is thrown as `formatAndThrowApiError` shapes it: the API's message
// for a 4xx, a generic one otherwise, with `status` and `details` on it. One that never got a response
// (offline, cancelled) has nothing to format, so the axios error goes through as it is.
export const fetchData = async (url) => {
  try {
    const result = await axios.get(url);
    return result.data;
  } catch (error) {
    if (!error.response) throw error;
    formatAndThrowApiError(error);
  }
};
